import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import planService, { Plan } from '../../services/plan.service';

interface FormattedPlan {
  _id: string;
  name: string;
  price: number;
  duration: number;
  description: string;
  features: {
    name: string;
    included: boolean;
  }[];
  maxProjects: number;
  maxUsers: number;
  status: 'active' | 'inactive';
  createdAt?: Date;
  updatedAt?: Date;
}

interface PlanDialogState {
  open: boolean;
  plan: FormattedPlan | null;
  isNew: boolean;
}

interface AlertState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const formatPlanForDisplay = (plan: Plan): FormattedPlan => {
  return {
    ...plan,
    features: plan.features.map(feature => ({ name: feature, included: true })),
  };
};

const formatPlanForApi = (plan: FormattedPlan): Partial<Plan> => {
  return {
    name: plan.name,
    description: plan.description,
    features: plan.features.filter(f => f.included).map(f => f.name),
    price: plan.price,
    duration: plan.duration,
    maxProjects: plan.maxProjects,
    maxUsers: plan.maxUsers,
    status: plan.status,
  };
};

const Plans: React.FC = () => {
  // State
  const [plans, setPlans] = useState<FormattedPlan[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogState, setDialogState] = useState<PlanDialogState>({
    open: false,
    plan: null,
    isNew: false,
  });
  const [alert, setAlert] = useState<AlertState>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState<boolean>(false);
  const [planToDelete, setPlanToDelete] = useState<string | null>(null);
  const theme = useTheme();
  // Fetch plans
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await planService.getPlans();
      setPlans(response.data.map(plan => formatPlanForDisplay(plan)));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to fetch plans');
      console.error('Error fetching plans:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle edit plan
  const handleEditPlan = (plan: FormattedPlan) => {
    setDialogState({
      open: true,
      plan: { ...plan },
      isNew: false,
    });
  };

  // Handle add plan
  const handleAddPlan = () => {
    const newPlan: FormattedPlan = {
      _id: '',
      name: '',
      price: 0,
      duration: 30,
      description: '',
      features: [
        { name: 'Feature 1', included: true },
        { name: 'Feature 2', included: true },
        { name: 'Feature 3', included: false },
      ],
      maxProjects: 10,
      maxUsers: 10,
      status: 'active',
    };

    setDialogState({
      open: true,
      plan: newPlan,
      isNew: true,
    });
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setDialogState({
      open: false,
      plan: null,
      isNew: false,
    });
  };

  // Handle save plan
  const handleSavePlan = async () => {
    if (!dialogState.plan) return;

    try {
      const planData = formatPlanForApi(dialogState.plan);

      if (dialogState.isNew) {
        const response = await planService.createPlan(planData);
        const newFormattedPlan = formatPlanForDisplay(response);
        setPlans([...plans, newFormattedPlan]);
        setAlert({
          open: true,
          message: 'Plan created successfully',
          severity: 'success',
        });
      } else {
        const response = await planService.updatePlan(dialogState.plan._id, planData);
        const updatedFormattedPlan = formatPlanForDisplay(response);
        setPlans(
          plans.map((plan) => (plan._id === updatedFormattedPlan._id ? updatedFormattedPlan : plan))
        );
        setAlert({
          open: true,
          message: 'Plan updated successfully',
          severity: 'success',
        });
      }

      handleCloseDialog();
    } catch (err: any) {
      setAlert({
        open: true,
        message: err?.response?.data?.message || 'Failed to save plan',
        severity: 'error',
      });
      console.error('Error saving plan:', err);
    }
  };

  // Handle toggle plan status
  const handleTogglePlanStatus = async (id: string, currentStatus: 'active' | 'inactive') => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      const response = await planService.togglePlanStatus(id, newStatus);
      const updatedFormattedPlan = formatPlanForDisplay(response);

      setPlans(
        plans.map((plan) => (plan._id === id ? updatedFormattedPlan : plan))
      );

      setAlert({
        open: true,
        message: `Plan ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`,
        severity: 'success',
      });
    } catch (err: any) {
      setAlert({
        open: true,
        message: err?.response?.data?.message || 'Failed to update plan status',
        severity: 'error',
      });
      console.error('Error updating plan status:', err);
    }
  };

  // Handle feature change
  const handleFeatureChange = (index: number, included: boolean) => {
    if (!dialogState.plan) return;

    const updatedFeatures = [...dialogState.plan.features];
    updatedFeatures[index] = { ...updatedFeatures[index], included };

    setDialogState({
      ...dialogState,
      plan: {
        ...dialogState.plan,
        features: updatedFeatures,
      },
    });
  };

  // Handle add feature
  const handleAddFeature = () => {
    if (!dialogState.plan) return;

    setDialogState({
      ...dialogState,
      plan: {
        ...dialogState.plan,
        features: [
          ...dialogState.plan.features,
          { name: '', included: true },
        ],
      },
    });
  };

  // Handle remove feature
  const handleRemoveFeature = (index: number) => {
    if (!dialogState.plan) return;

    const updatedFeatures = [...dialogState.plan.features];
    updatedFeatures.splice(index, 1);

    setDialogState({
      ...dialogState,
      plan: {
        ...dialogState.plan,
        features: updatedFeatures,
      },
    });
  };

  // Handle delete plan
  const handleDeletePlan = (id: string) => {
    setPlanToDelete(id);
    setDeleteConfirmDialogOpen(true);
  };

  // Confirm delete plan
  const confirmDeletePlan = async () => {
    if (!planToDelete) return;

    try {
      await planService.deletePlan(planToDelete);
      setPlans(plans.filter(plan => plan._id !== planToDelete));
      setAlert({
        open: true,
        message: 'Plan deleted successfully',
        severity: 'success',
      });
    } catch (err: any) {
      setAlert({
        open: true,
        message: err?.response?.data?.message || 'Failed to delete plan',
        severity: 'error',
      });
      console.error('Error deleting plan:', err);
    } finally {
      setDeleteConfirmDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  // Handle close alert
  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    });
  };

  // Render
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 700,
            color: theme.palette.primary.main,
            position: 'relative',
            display: 'inline-block',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 40,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: 2,
            }
          }}
        >
          Plans
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddPlan}
        >
          Add Plan
        </Button>
      </Box>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="textSecondary" sx={{ mb: 2 }}>
            No plans found. Click "Add Plan" to create your first plan.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} lg={3} key={plan._id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  opacity: plan.status === 'active' ? 1 : 0.7,
                  position: 'relative',
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="h5" component="div">
                      {plan.name}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="h4" color="primary" sx={{ mt: 1 }}>
                      ${plan.price}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        /{plan.duration} days
                      </Typography>
                    </Typography>
                  }
                  action={
                    <Box>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEditPlan(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDeletePlan(plan._id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  }
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {plan.description}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Max Projects:</strong> {plan.maxProjects}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Max Users:</strong> {plan.maxUsers}
                    </Typography>
                  </Box>

                  <List dense>
                    {plan.features.filter(f => f.included).map((feature, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature.name} />
                      </ListItem>
                    ))}
                  </List>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={plan.status === 'active'}
                          onChange={() => handleTogglePlanStatus(plan._id, plan.status)}
                          color="primary"
                        />
                      }
                      label={plan.status === 'active' ? "Active" : "Inactive"}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Edit/Add Plan Dialog */}
      <Dialog open={dialogState.open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogState.isNew ? 'Add New Plan' : `Edit ${dialogState.plan?.name} Plan`}
        </DialogTitle>
        <DialogContent dividers>
          {dialogState.plan && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Plan Name"
                  value={dialogState.plan.name}
                  onChange={(e) =>
                    setDialogState({
                      ...dialogState,
                      plan: { ...dialogState.plan!, name: e.target.value },
                    })
                  }
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Price"
                  type="number"
                  value={dialogState.plan.price}
                  onChange={(e) =>
                    setDialogState({
                      ...dialogState,
                      plan: { ...dialogState.plan!, price: parseFloat(e.target.value) || 0 },
                    })
                  }
                  InputProps={{
                    startAdornment: '$',
                  }}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  label="Duration (days)"
                  type="number"
                  value={dialogState.plan.duration}
                  onChange={(e) =>
                    setDialogState({
                      ...dialogState,
                      plan: { ...dialogState.plan!, duration: parseInt(e.target.value) || 30 },
                    })
                  }
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={dialogState.plan.description}
                  onChange={(e) =>
                    setDialogState({
                      ...dialogState,
                      plan: { ...dialogState.plan!, description: e.target.value },
                    })
                  }
                  margin="normal"
                  multiline
                  rows={3}
                  required
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={dialogState.plan.status}
                    onChange={(e) =>
                      setDialogState({
                        ...dialogState,
                        plan: { ...dialogState.plan!, status: e.target.value as 'active' | 'inactive' },
                      })
                    }
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Projects"
                  type="number"
                  value={dialogState.plan.maxProjects}
                  onChange={(e) =>
                    setDialogState({
                      ...dialogState,
                      plan: { ...dialogState.plan!, maxProjects: parseInt(e.target.value) || 0 },
                    })
                  }
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Users"
                  type="number"
                  value={dialogState.plan.maxUsers}
                  onChange={(e) =>
                    setDialogState({
                      ...dialogState,
                      plan: { ...dialogState.plan!, maxUsers: parseInt(e.target.value) || 0 },
                    })
                  }
                  margin="normal"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Plan Features
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  {dialogState.plan.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2
                      }}
                    >
                      <TextField
                        fullWidth
                        label={`Feature ${index + 1}`}
                        value={feature.name}
                        onChange={(e) => {
                          const updatedFeatures = [...dialogState.plan!.features];
                          updatedFeatures[index] = {
                            ...updatedFeatures[index],
                            name: e.target.value
                          };
                          setDialogState({
                            ...dialogState,
                            plan: {
                              ...dialogState.plan!,
                              features: updatedFeatures,
                            },
                          });
                        }}
                        sx={{ mr: 2 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={feature.included}
                            onChange={() => handleFeatureChange(index, !feature.included)}
                          />
                        }
                        label={feature.included ? "Included" : "Not Included"}
                        sx={{ width: 200 }}
                      />
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveFeature(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddFeature}
                  >
                    Add Feature
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePlan}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialogOpen} onClose={() => setDeleteConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this plan? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeletePlan} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Plans;